import { LiveData, OnEvent, Service } from '@toeverything/infra';

import { AccountChanged } from '../../cloud/events/account-changed';
import { ServerStarted } from '../../cloud/events/server-started';
import { RealtimeLiveQuery } from '../../cloud/realtime/live-query';
import type { AuthService } from '../../cloud/services/auth';
import { ApplicationFocused } from '../../lifecycle';
import type { NbstoreService } from '../../storage';
import type { NotificationStore } from '../stores/notification';

@OnEvent(ApplicationFocused, s => s.handleApplicationFocused)
@OnEvent(ServerStarted, s => s.handleServerStarted)
@OnEvent(AccountChanged, s => s.handleAccountChanged)
export class NotificationCountService extends Service {
  constructor(
    private readonly store: NotificationStore,
    private readonly authService: AuthService,
    private readonly nbstoreService: NbstoreService
  ) {
    super();
  }

  loggedIn$ = LiveData.from(
    this.authService.session.status$.map(v => v === 'authenticated'),
    this.authService.session.status$.value === 'authenticated'
  );

  readonly count$ = LiveData.from(this.store.watchNotificationCountCache(), 0);
  readonly isLoading$ = new LiveData(false);
  readonly error$ = new LiveData<any>(null);
  private retryTimer?: ReturnType<typeof setTimeout>;
  private retryDelay = 1_000;
  private disposed = false;
  private readonly liveQuery = new RealtimeLiveQuery({
    request: signal => this.requestCount(signal),
    subscribe: () =>
      this.nbstoreService.realtime.subscribe('notification.count.changed', {}),
    applySnapshot: result => this.setCount(result.count),
    applyEvent: event => {
      this.setCount(event.count);
      return 'applied';
    },
    onError: error => {
      this.error$.setValue(error);
      // Login and worker realtime configuration complete independently.
      // A failed subscription must recover once that context is ready.
      if (!this.disposed && this.loggedIn$.value && !this.retryTimer) {
        this.retryTimer = setTimeout(() => {
          this.retryTimer = undefined;
          this.subscribe();
        }, this.retryDelay);
        this.retryDelay = Math.min(this.retryDelay * 2, 30_000);
      }
    },
  });

  revalidate = () => {
    if (!this.loggedIn$.value) {
      this.setCount(0);
      return;
    }
    this.liveQuery.revalidate();
  };

  handleApplicationFocused() {
    this.revalidate();
  }

  handleServerStarted() {
    this.subscribe();
    this.revalidate();
  }

  handleAccountChanged() {
    this.subscribe();
    this.revalidate();
  }

  setCount(count: number) {
    this.retryDelay = 1_000;
    this.error$.setValue(null);
    this.store.setNotificationCountCache(count);
  }

  override dispose(): void {
    this.disposed = true;
    this.clearRetry();
    super.dispose();
    this.liveQuery.dispose();
  }

  private clearRetry() {
    if (this.retryTimer !== undefined) clearTimeout(this.retryTimer);
    this.retryTimer = undefined;
  }

  private subscribe() {
    this.clearRetry();
    if (this.disposed || !this.loggedIn$.value) {
      this.liveQuery.stop();
      this.setCount(0);
      return;
    }
    this.liveQuery.start();
  }

  private async requestCount(signal: AbortSignal) {
    this.isLoading$.setValue(true);
    try {
      return await this.nbstoreService.realtime.request(
        'notification.count.get',
        {},
        { signal, timeoutMs: 10000 }
      );
    } finally {
      this.isLoading$.setValue(false);
    }
  }
}
